/**
 * Google Identity Services loader.
 *
 * Shared by the header and the Settings panel. They can mount in either order,
 * so this has to be idempotent: a second caller must reuse the in-flight load
 * rather than injecting a second <script>, which would re-initialise the library
 * underneath whichever button rendered first.
 *
 * Loaded on demand rather than from index.html — nobody who never signs in
 * should pay for it, and it keeps "no third-party scripts" true until the user
 * opts in.
 *
 * CSP: accounts.google.com must be allowed in script-src, style-src, frame-src
 * AND connect-src. Miss style-src and the script loads fine but the button
 * renders nothing, with only a console error to explain it.
 */

const SRC = "https://accounts.google.com/gsi/client";

let pending = null;

export function loadGis() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (pending) return pending;

  pending = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SRC}"]`);
    const el = existing || document.createElement("script");

    const done = () => (window.google?.accounts?.id ? resolve() : reject(new Error("Google sign-in did not initialise.")));
    const failed = () => {
      pending = null; // Allow a retry — the user may just have been offline.
      reject(new Error("Could not load Google sign-in."));
    };

    el.addEventListener("load", done, { once: true });
    el.addEventListener("error", failed, { once: true });

    if (!existing) {
      el.src = SRC;
      el.async = true;
      el.defer = true;
      document.head.appendChild(el);
    }
  });

  return pending;
}
